export const STARTUP_TIMEOUT = 10000;
export const CHECK_INTERVAL = 5 * 60 * 1000;
export const EVENT_THROTTLE = 30000;
export const CONFIRM_DELAY = 10000;
export const ATTEMPT_PREFIX = 'rezrate.release.attempt.';

export function validRelease(value) {
  return (
    !!value &&
    value.schemaVersion === 1 &&
    typeof value.buildId === 'string' &&
    /^[a-zA-Z0-9._-]{1,160}$/.test(value.buildId) &&
    typeof value.commit === 'string' &&
    /^[a-f0-9]{40}$/i.test(value.commit) &&
    typeof value.builtAt === 'string' &&
    Number.isFinite(Date.parse(value.builtAt))
  );
}

// Application policy. Browser/network behavior is injected by the adapter.
export function createReleaseController({
  loadedBuild,
  readRelease,
  attempts,
  reload,
  now = Date.now,
  schedule = setTimeout,
  cancel = clearTimeout,
}) {
  let state = { ready: false, loadedBuild, latestBuild: null, lastCheckedAt: null, recovery: false, error: null };
  let listeners = new Set();
  let started = false;
  let stopped = false;
  let startup = true;
  let inFlight;
  let startupTimer;
  let confirmationTimer;
  let pending;
  let lastEventAt = -Infinity;
  const publish = (patch) => {
    if (stopped) return;
    state = { ...state, ...patch };
    listeners.forEach((fn) => fn(state));
  };
  const open = () => {
    startup = false;
    cancel(startupTimer);
    publish({ ready: true });
  };
  const recordAttempt = (id) => {
    try {
      const key = ATTEMPT_PREFIX + id;
      if (attempts.getItem(key)) return false;
      attempts.setItem(key, '1');
      return attempts.getItem(key) === '1';
    } catch (_) {
      return false;
    }
  };
  const confirmed = (release) => {
    if (stopped) return;
    publish({ latestBuild: release.buildId });
    if (startup) {
      // Persist before navigation. The watchdog still opens the UI if navigation fails.
      if (recordAttempt(release.buildId)) {
        startup = false;
        publish({ recovery: true });
        try {
          reload();
        } catch (_) {
          open();
        }
      } else {
        publish({ recovery: true });
        open();
      }
    }
  };
  const observe = async () => {
    try {
      const release = await readRelease();
      if (stopped) return;
      if (!validRelease(release)) throw new Error('Invalid release');
      publish({ lastCheckedAt: new Date(now()).toISOString(), error: null });
      if (release.buildId === loadedBuild) {
        pending = null;
        cancel(confirmationTimer);
        publish({ latestBuild: null, recovery: false });
        if (startup) open();
        return;
      }
      if (startup) {
        const second = await readRelease();
        if (stopped) return;
        if (!validRelease(second) || second.buildId !== release.buildId) {
          if (startup) open();
          return;
        }
        // A response arriving after the gate opened cannot cause automatic navigation.
        if (startup) {
          confirmed(second);
          return;
        }
      }
      if (pending && pending.id === release.buildId && now() - pending.at >= CONFIRM_DELAY) {
        confirmed(release);
        pending = null;
      } else {
        pending = { id: release.buildId, at: now() };
        cancel(confirmationTimer);
        confirmationTimer = schedule(() => check(), CONFIRM_DELAY);
      }
    } catch (_) {
      pending = null;
      publish({ error: 'Unable to check for an update. You can continue working.' });
      if (startup) open();
    }
  };
  const check = () => {
    if (stopped) return Promise.resolve();
    if (!inFlight)
      inFlight = observe().finally(() => {
        inFlight = null;
      });
    return inFlight;
  };
  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    start() {
      if (started) return;
      started = true;
      startupTimer = schedule(open, STARTUP_TIMEOUT);
      check();
    },
    check,
    event() {
      if (now() - lastEventAt < EVENT_THROTTLE) return;
      lastEventAt = now();
      return check();
    },
    async refresh() {
      try {
        const release = await readRelease();
        if (stopped) return false;
        if (!validRelease(release) || release.buildId !== state.latestBuild) throw new Error('Release changed');
        // A manual refresh must not create an automatic refresh loop on the next load.
        try {
          attempts.setItem(ATTEMPT_PREFIX + release.buildId, '1');
        } catch (_) {
          /* Manual navigation is intentional. */
        }
        reload();
        return true;
      } catch (_) {
        publish({ error: 'The update could not be verified. Please try again.', recovery: true });
        return false;
      }
    },
    stop() {
      stopped = true;
      cancel(startupTimer);
      cancel(confirmationTimer);
      listeners.clear();
    },
  };
}
